/**
 * Act 1 of the story (docs/spellweave-modes.md §1): the Drowned Cloister, rooms
 * 1-5 west to east, one continuous map.
 *
 * Gatehouse: light both braziers to open the rune door; the first one to
 * catch wakes spiders in the floor cracks, and the door stays shut while
 * they live. Fire lights a brazier; water, cold, steam or a Rain puts it out.
 * Flooded Hall: a deep channel cuts the hall in two. Cold freezes it into an
 * ice bridge for a while; fire melts it. Rogues wade the shallows on the far
 * side, where lightning runs through the water to everyone standing in it;
 * rangers shoot across.
 * Boiler Room: fill the boiler with water, then keep it hot until the
 * pressure lifts the portcullis; water or cold on it cools it, so careless
 * friends work against you. Bombers and rogues come in three pushes.
 * Bramble Garden: a thicket of brambles walls the garden off. Fire spreads
 * through them and leaves ash to walk on; the shaman keeps the brambles
 * near it wet, and wet brambles don't catch.
 * Undercroft: a chasm. Lava fills it with molten rock and cold sets the rock
 * to stone, a bridge; anyone knocked in falls. Skeletons climb out of the
 * crypt on both sides.
 * Sunken Chapel: Mother Mire (server enemies/mire.ts). Her water beam blows
 * up when a lightning beam crosses it; from 60% she floods the chapel and
 * sinks into her pool to heal unless it is frozen; from 25% the Everflame
 * behind her wakes, and lit it boils the flood. Her death relights it and
 * ends the act.
 *
 * Tiles: '#' wall, '.' floor, '~' shallow water, '=' deep water, '%'
 * brambles, '_' chasm (level.ts).
 */

import type { LevelDef } from './level.js';
import { PLAYER_SPAWN_RING } from './constants.js';

export const CLOISTER_LEVEL: LevelDef = {
  format: 1,
  id: 'cloister',
  name: 'The Drowned Cloister',
  w: 248,
  h: 30,
  tiles: {
    rows: [
      '########################################################################################################################################################################################################################################################',
      '#####################################............~~~======~~~..............####....................................######...............%%%%%%%%%%...............############################################........................................###',
      '#####################################............~~~======~~~..............####....................................######...............%%%%%%%%%%...............############################################........................................###',
      '#####################################............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#####################################............~~~======~~~..............####....................................######.....%%%.......%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######.....%%%.......%%%%%%%%%%.......%%%.....####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%.......%%%.....####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######................~~~~~~~~................###',
      '#..............................######............~~~======~~~..............####....................................######..........%%...%%%%%%%%%%...............####.............___..................######................~======~................###',
      '#................................................~~~======~~~......................................................................%%...%%%%%%%%%%................................___........................................~======~................###',
      '#................................................~~~======~~~...........................................................................%%%%%%%%%%................................___........................................~======~................###',
      '#................................................~~~======~~~...........................................................................%%%%%%%%%%................................___........................................~======~................###',
      '#................................................~~~======~~~...........................................................................%%%%%%%%%%................................___........................................~======~................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######................~======~................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######................~~~~~~~~................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######...............%%%%%%%%%%.....%%%%......####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######......%%.......%%%%%%%%%%.....%%%%......####.............___..................######........................................###',
      '#..............................######............~~~======~~~..............####....................................######......%%.......%%%%%%%%%%...............####.............___..................######........................................###',
      '#####################################............~~~======~~~..............####....................................######......%%.......%%%%%%%%%%...............####.............___..................######........................................###',
      '#####################################............~~~======~~~..............####....................................######...............%%%%%%%%%%...............####.............___..................######........................................###',
      '#####################################............~~~======~~~..............####....................................######...............%%%%%%%%%%...............############################################........................................###',
      '#####################################............~~~======~~~..............####....................................######...............%%%%%%%%%%...............############################################........................................###',
      '########################################################################################################################################################################################################################################################',
    ],
  },
  // the boiler's footing (drawn with the boiler)
  solids: [{ shape: { k: 'circle', x: 97, y: 15, r: 1 }, art: 'footing' }],
  water: [],
  rooms: [
    { id: 'gatehouse', rect: { x: 1, y: 5, w: 30, h: 20 } },
    { id: 'hall', rect: { x: 33, y: 1, w: 42, h: 28 } },
    { id: 'boiler', rect: { x: 79, y: 1, w: 36, h: 28 } },
    { id: 'garden', rect: { x: 121, y: 1, w: 40, h: 28 } },
    { id: 'undercroft', rect: { x: 165, y: 3, w: 34, h: 24 } },
    { id: 'chapel', rect: { x: 205, y: 1, w: 40, h: 28 } },
  ],
  doors: [
    { id: 'rune', rect: { x: 31, y: 13, w: 2, h: 4 }, art: 'rune-door' },
    { id: 'boiler-in', rect: { x: 77, y: 13, w: 2, h: 4 }, open: true, art: 'door' },
    { id: 'portcullis', rect: { x: 115, y: 13, w: 2, h: 4 }, art: 'portcullis' },
    { id: 'crypt-in', rect: { x: 163, y: 13, w: 2, h: 4 }, open: true, art: 'door' },
    { id: 'chapel-in', rect: { x: 203, y: 13, w: 2, h: 4 }, open: true, art: 'door' },
  ],
  spawn: { x: 6, y: 15, ring: PLAYER_SPAWN_RING },
  gates: [
    // 0-3: cracks in the Gatehouse floor
    { x: 12, y: 7 },
    { x: 20, y: 22 },
    { x: 8, y: 21 },
    { x: 22, y: 8 },
    // 4-6: the far bank of the Flooded Hall
    { x: 70, y: 4 },
    { x: 70, y: 25 },
    { x: 67, y: 15 },
    // 7-10: the Boiler Room's corners
    { x: 90, y: 2 },
    { x: 105, y: 2 },
    { x: 90, y: 28 },
    { x: 105, y: 28 },
    // 11: the shaman, before the thicket; 12-13: tree spirits behind it; 14-15: spiders in the beds
    { x: 133, y: 15 },
    { x: 150, y: 9 },
    { x: 150, y: 24 },
    { x: 124, y: 3 },
    { x: 124, y: 27 },
    // 16-19: the crypt, both sides of the chasm
    { x: 170, y: 4 },
    { x: 174, y: 26 },
    { x: 186, y: 5 },
    { x: 190, y: 25 },
    // 20-23: cracks in the chapel's corners (her spiders); 24: Mother Mire, between her pool and the Everflame
    { x: 209, y: 3 },
    { x: 240, y: 3 },
    { x: 209, y: 27 },
    { x: 240, y: 27 },
    { x: 234, y: 15 },
  ],
  dummies: [],
  art: { plates: ['cloister', 'cloister-east', 'cloister-chapel'] },
  objects: [
    { k: 'brazier', id: 'north', x: 26, y: 10 },
    { k: 'brazier', id: 'south', x: 26, y: 20 },
    { k: 'boiler', id: 'boiler', x: 97, y: 15 },
    { k: 'brazier', id: 'everflame', x: 241, y: 15 },
    { k: 'rune-stone', id: 'gate-stone', x: 4, y: 9 },
    { k: 'rune-stone', id: 'hall-stone', x: 40, y: 10 },
    { k: 'rune-stone', id: 'boiler-stone', x: 82, y: 10 },
    { k: 'rune-stone', id: 'garden-stone', x: 124, y: 10 },
    { k: 'rune-stone', id: 'undercroft-stone', x: 168, y: 10 },
    { k: 'rune-stone', id: 'chapel-stone', x: 208, y: 9 },
  ],
  story: {
    act: 'Act 1: The Drowned Cloister',
    rooms: [
      {
        room: 'gatehouse',
        title: 'The Gatehouse',
        say: [
          'The cloister is underwater. That is not how cloisters are supposed to work.',
          'Fire goes in the braziers. Not in your friends. Mostly.',
        ],
        objective: 'Light both braziers',
        spawn: { on: 'lit', groups: [{ kind: 'spider', count: 6, gates: [0, 1, 2, 3] }], say: ['Something under the floor heard that.'] },
        light: { braziers: ['north', 'south'], opens: 'rune', say: ['The rune door remembers fire. It opens.'], after: 'Through the rune door' },
      },
      {
        room: 'hall',
        title: 'The Flooded Hall',
        say: [
          'Too deep to wade. Cold turns water into floor.',
          "You know this. You've done it to each other.",
        ],
        objective: 'Cross the hall',
        spawn: { on: 'enter', groups: [{ kind: 'rogue', count: 4, gates: [4, 5, 6] }, { kind: 'ranger', count: 3, gates: [4, 5, 6] }] },
      },
      {
        room: 'boiler',
        title: 'The Boiler Room',
        say: [
          'Water in, fire under, wait.',
          'Do not spray the boiler while it heats. Someone will.',
        ],
        objective: 'Fill the boiler with water',
        lock: ['boiler-in'],
        boiler: {
          id: 'boiler',
          opens: 'portcullis',
          seconds: 30,
          pushes: [
            [{ kind: 'rogue', count: 3, gates: [7, 8, 9, 10] }],
            [{ kind: 'bomber', count: 3, gates: [7, 8, 9, 10] }, { kind: 'rogue', count: 2, gates: [9, 10] }],
            [{ kind: 'bomber', count: 4, gates: [7, 8, 9, 10] }, { kind: 'rogue', count: 3, gates: [7, 8] }],
          ],
          say: ['The portcullis lifts. The boiler is somebody else\'s problem now.'],
          after: 'Through the portcullis',
        },
      },
      {
        room: 'garden',
        title: 'The Bramble Garden',
        say: [
          "Brambles burn. Wet brambles don't.",
          'The shaman knows this.',
        ],
        objective: 'Burn a way through',
        lock: ['portcullis'],
        spawn: {
          on: 'enter',
          groups: [
            { kind: 'shaman', count: 1, gates: [11] },
            { kind: 'treespirit', count: 3, gates: [12, 13] },
            { kind: 'spider', count: 4, gates: [14, 15] },
          ],
        },
      },
      {
        room: 'undercroft',
        title: 'The Undercroft',
        say: [
          'Fire, then earth: lava. Lava fills holes. Cold makes it stay filled.',
          'The chasm is deeper than it looks. It looks very deep.',
        ],
        objective: 'Bridge the chasm',
        lock: ['crypt-in'],
        spawn: { on: 'enter', groups: [{ kind: 'skeleton', count: 8, gates: [16, 17, 18, 19] }], say: ['The dead down here were not expecting company. They are adapting.'] },
        bridge: { say: ['Stone. Somebody here can be taught.'], after: 'Over the bridge' },
      },
      {
        room: 'chapel',
        title: 'The Sunken Chapel',
        say: [
          'Mother Mire. She has been expecting you.',
          'She is just very wet about it.',
          'Her water beam hates lightning. Cross it with one and stand well back.',
        ],
        objective: 'Beat Mother Mire',
        lock: ['chapel-in'],
        boss: {
          kind: 'mire',
          gate: 24,
          calls: { kind: 'spider', count: 4, gates: [20, 21, 22, 23] },
          everflame: 'everflame',
          phases: [
            ['She is calling the tide in. Everyone gets wet.', 'Cold on her pool and she has nowhere to hide.'],
            ['The Everflame stirs behind her. It remembers what it was for.', 'Light it. Then let the water do the rest.'],
          ],
          objectives: ['Beat Mother Mire', 'Freeze her pool, then hit her', 'Light the Everflame'],
          win: [
            'The Everflame burns again. The cloister is merely damp now.',
            'Act 1 is done. The Tide will not forget this. Neither will your eyebrows.',
          ],
        },
      },
    ],
  },
};
